import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useOmlMutations } from '@/hooks/useOml';
import { OML_ALLOWED_MIME, OML_MAX_FILE_BYTES } from '../../../../shared/types/oml';
import { formatFileSize } from '@/lib/utils';

/** Preview de un adjunto (imagen inline; PDF como link con ícono). URL prefirmada de 1 h. */
export function AssetPreview({ accountId, assetId, compact = false }: { accountId: string; assetId: string; compact?: boolean }) {
  const { t } = useTranslation();
  const { data } = trpc.oml.assets.downloadUrl.useQuery({ accountId, assetId }, { staleTime: 50 * 60_000, retry: false });
  if (!data) return <span className="text-xs text-muted-foreground">{t('oml.assets.loading', 'cargando adjunto…')}</span>;
  const isImage = data.asset.contentType.startsWith('image/');
  if (isImage) {
    return (
      <a href={data.url} target="_blank" rel="noopener noreferrer" title={data.asset.name} className="inline-block">
        <img src={data.url} alt={data.asset.name} className={compact ? 'h-10 w-10 object-cover rounded border' : 'max-h-48 rounded border'} />
      </a>
    );
  }
  return (
    <a href={data.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
      <FileText className="h-4 w-4" />{compact ? 'PDF' : `${data.asset.name} · ${formatFileSize(data.asset.size)}`}
    </a>
  );
}

/** Subida de adjuntos (§5.4): PNG/JPG/SVG/PDF, 5 MB, directo a S3 por URL prefirmada; guarda los ids en el campo. */
export function AssetField({ accountId, value, onChange, multiple, disabled }: { accountId: string; value: string[]; onChange: (ids: string[]) => void; multiple: boolean; disabled?: boolean }) {
  const { t } = useTranslation();
  const { uploadUrl } = useOmlMutations();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const ids: string[] = [];
      for (const file of Array.from(files)) {
        if (!OML_ALLOWED_MIME.includes(file.type)) { toast.error(t('oml.assets.bad_type', 'Solo PNG, JPG, SVG o PDF. Los videos y los decks se linkean.')); continue; }
        if (file.size > OML_MAX_FILE_BYTES) { toast.error(t('oml.assets.too_big', '{{name}} supera los 5 MB', { name: file.name })); continue; }
        const { asset, url } = await uploadUrl.mutateAsync({ accountId, name: file.name, contentType: file.type, size: file.size });
        const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        if (!res.ok) { toast.error(t('oml.assets.upload_failed', 'No se pudo subir {{name}}', { name: file.name })); continue; }
        ids.push(asset.id);
      }
      if (ids.length) onChange(multiple ? [...value, ...ids] : [ids[ids.length - 1]]);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {value.map((id) => (
          <div key={id} className="relative inline-flex items-center gap-1 rounded border px-1.5 py-1">
            <AssetPreview accountId={accountId} assetId={id} compact />
            {!disabled && <button type="button" onClick={() => onChange(value.filter((x) => x !== id))} className="text-muted-foreground hover:text-destructive" aria-label={t('common.remove', 'Quitar')}><X className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
      </div>
      {!disabled && (
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" className="hidden" accept={OML_ALLOWED_MIME.join(',')} multiple={multiple} onChange={(e) => onFiles(e.target.files)} />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? <ImageIcon className="h-3.5 w-3.5 mr-1 animate-pulse" /> : <Paperclip className="h-3.5 w-3.5 mr-1" />}
            {busy ? t('oml.assets.uploading', 'Subiendo…') : multiple ? t('oml.assets.add', 'Agregar adjuntos') : t('oml.assets.choose', 'Elegir archivo')}
          </Button>
          <span className="text-xs text-muted-foreground">{t('oml.assets.hint', 'PNG, JPG, SVG o PDF · máx. 5 MB')}</span>
        </div>
      )}
    </div>
  );
}
